import { utc } from "@date-fns/utc";
import { differenceInCalendarDays, isValid, parseJSON } from "date-fns";
import z from "zod";

import { BidResponseSortBy, BidResponseSortOrder } from "../../../lib/types/graphQLEnums";

export function getBidRoute(bidId: string): string {
  return `/bids/${bidId}`;
}

export function getBidResponseRoute(bidId: string | undefined, bidResponseId: string): string {
  if (bidId) {
    return `/bids/${bidId}/bid_responses/${bidResponseId}`;
  }

  return `/bid_responses/${bidResponseId}`;
}

export function isValidSortField(field?: string): BidResponseSortBy | undefined {
  if (field && validSortFields.includes(field as BidResponseSortBy)) {
    return field as BidResponseSortBy;
  }
  return undefined;
}

export const validSortFields: BidResponseSortBy[] = [
  BidResponseSortBy.Title,
  BidResponseSortBy.CreatedAt,
  BidResponseSortBy.Stage,
  BidResponseSortBy.Reference,
  BidResponseSortBy.Section,
  BidResponseSortBy.DueDate,
];

export type BidResponseSortState = {
  field: BidResponseSortBy;
  order: BidResponseSortOrder;
};
export function mapSortFieldToEnum(field: string): BidResponseSortBy {
  switch (field) {
    case "title":
      return BidResponseSortBy.Title;
    case "stage":
      return BidResponseSortBy.Stage;
    case "createdAt":
      return BidResponseSortBy.CreatedAt;
    case "reference":
      return BidResponseSortBy.Reference;
    case "section":
      return BidResponseSortBy.Section;
    case "dueDate":
      return BidResponseSortBy.DueDate;
    default:
      return BidResponseSortBy.CreatedAt;
  }
}

export function mapSortOrderToEnum(order: "ASC" | "DESC" | null | undefined): BidResponseSortOrder {
  return order === "ASC" ? BidResponseSortOrder.Asc : BidResponseSortOrder.Desc;
}

export const BidResponsesFiltersSchema = z.object({
  bidId: z.uuid().optional(),
  textSearch: z.string().optional(),
  stageIds: z.array(z.uuid()).optional(),
  assigneeIds: z.array(z.uuid()).optional(),
  ids: z.array(z.uuid()).optional(),
  order: z.enum(BidResponseSortOrder),
  orderBy: z.enum(BidResponseSortBy),
});
export type BidResponsesFilters = z.infer<typeof BidResponsesFiltersSchema>;

/**
 * Removes inline citation markers like `[1]`, `[2]` from AI-generated text,
 * along with any whitespace immediately preceding them, so the resulting text
 * does not contain double spaces or stranded spaces before punctuation.
 */
export function stripCitationBrackets(text: string): string {
  return text.replace(/\s*\[\d+\]/g, "").trimStart();
}

/** A question is "due soon" once it is this many days away, or closer. */
export const DUE_SOON_DAYS = 3;

export type DueDateStatus = "none" | "upcoming" | "dueSoon" | "overdue";

/**
 * Compares calendar days in UTC, not instants — a due date is a whole day stored at
 * midnight UTC, so a question due today stays "due soon" until that day is over.
 */
export function getDueDateStatus(
  dueDate: string | null | undefined,
  now: Date = new Date(),
): DueDateStatus {
  const parsed = parseDueDate(dueDate);
  if (!parsed) {
    return "none";
  }

  const daysUntilDue = differenceInCalendarDays(parsed, now, { in: utc });
  if (daysUntilDue < 0) {
    return "overdue";
  }
  return daysUntilDue <= DUE_SOON_DAYS ? "dueSoon" : "upcoming";
}

// Coercion rebuilds the Date, which matters: the calendar hands back a TZDate whose own
// toISOString() emits "+00:00", and the API only accepts the Z form.
const dueDateIsoSchema = z.coerce.date().transform((date) => date.toISOString());

/** Normalises what the date field holds — an ISO string or a Date — into an ISO string. */
export function toDueDateIso(value: Date | string | null | undefined): string | null {
  if (!value) {
    return null;
  }
  const result = dueDateIsoSchema.safeParse(value);
  return result.success ? result.data : null;
}

function parseDueDate(dueDate: string | null | undefined) {
  if (!dueDate) {
    return null;
  }
  const parsed = parseJSON(dueDate, { in: utc });
  return isValid(parsed) ? parsed : null;
}
